import Datepicker from 'components/ui/Datepicker';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Grid from 'components/ui/Grid';
import useAppSelector from 'hooks/useAppSelector';
import useAppDispatch from 'hooks/useAppDispatch';
import { returnSelectors, setCreditLabel, setClaimReference, selectBulkReasonCode } from 'store/reducers/returnSlice';
import { decisionCodeSelectors } from 'store/reducers/decisionCodeSlice';
import { configSelectors } from 'store/reducers/configSlice';

type MetaCardsProps = {
    isCompleted: boolean;
    handleArrivalDateChange: (date: Date | [Date, Date] | null) => void;
    handleBulkReasonChange: (value: number) => void;
};

const MetaCards: React.FC<MetaCardsProps> = ({ isCompleted, handleArrivalDateChange, handleBulkReasonChange }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const salesContact = useAppSelector(returnSelectors.selectSelectedSalesContact);
    const ruleSetName = useAppSelector(decisionCodeSelectors.selectRuleSetName);
    const returnSlip = useAppSelector(returnSelectors.selectReturnSlip);
    const buttonStates = useAppSelector(returnSelectors.selectReturnButtonStates);
    const reasonCodes = useAppSelector(configSelectors.selectReasonCodesMap);
    const creditLabelOptions = useAppSelector(configSelectors.selectCreditLabelOptions);
    const bulkReasonCode = useAppSelector(selectBulkReasonCode);
    const cardCount = creditLabelOptions.length > 0 ? 3 : 5;

    return (
        <StyledGrid
            gap="10px"
            columns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: `repeat(${cardCount}, 1fr)` }}
            className="meta-cards-wrapper"
        >
            {/* ── Card 1: Customer info ── */}
            <Card justify="between">
                <CardLabel>{t('customer', 'Customer')}</CardLabel>

                <Text fontSize={14} bold as="span">
                    {salesContact.name ?? ''}
                </Text>

                <Text fontSize={12} as="span">
                    {`${salesContact.code}`}
                </Text>

                <Text fontSize={12} as="span">
                    {`${t('seller', 'Seller').toUpperCase()}: ${salesContact.salesContact}`}
                </Text>
            </Card>

            {/* ── Card 2: Customer group ── */}
            <Card justify="between">
                <CardLabel>{t('customerGroup', 'Customer group')}</CardLabel>

                <Text fontSize={14} bold as="span">
                    {salesContact.groupCode ?? '—'}
                </Text>

                <Text fontSize={12} as="span">
                    {`${salesContact.countryCode ?? '-'} / ${salesContact.currency ?? '-'}`}
                </Text>

                {ruleSetName && (
                    <Text fontSize={12} as="span">
                        {`${t('ruleSet', 'Rule set').toUpperCase()}: ${ruleSetName ?? '—'}`}
                    </Text>
                )}
            </Card>

            {/* ── Card 3: Arrival date ── */}
            <Card justify="between">
                <CardLabel>{t('arrivalDate', 'Arrival date')}</CardLabel>

                <DatePickerWrapper>
                    <Datepicker
                        selectedDate={returnSlip?.arrivalDate?.utc ? new Date(returnSlip.arrivalDate.utc) : null}
                        header={t('arrivalDate', 'Arrival date')}
                        onChange={handleArrivalDateChange}
                        showIcon
                        filter={'None'}
                        disabled={isCompleted}
                        maxDate={new Date()}
                    />
                </DatePickerWrapper>
            </Card>

            {/* ── Card 4: Credit label (conditional) ── */}
            {creditLabelOptions.length > 0 && (
                <Card justify="between">
                    <CardLabel>{t('creditLabel')}</CardLabel>

                    <StyledSelect
                        id="credit-label-select"
                        options={creditLabelOptions}
                        placeholder={t('selectCreditLabel')}
                        isDisabled={isCompleted}
                        value={creditLabelOptions.find((opt) => opt.value === returnSlip?.creditLabel) || null}
                        onChange={(selectedOption) => {
                            const selected = selectedOption as {
                                label: string;
                                value: string;
                            } | null;
                            dispatch(setCreditLabel(selected ? selected.value : null));
                        }}
                        isClearable
                    />
                </Card>
            )}

            {/* ── Card 5: Claim reference ── */}
            <Card justify="between">
                <CardLabel>{t('claimReference', "Customer's return reference")}</CardLabel>

                <StyledInput
                    id="claim-reference-input"
                    type="text"
                    maxLength={50}
                    placeholder={t('claimReferencePlaceholder', "Enter customer's return reference…")}
                    disabled={isCompleted}
                    value={returnSlip?.claimReference ?? ''}
                    onChange={(e) => {
                        dispatch(setClaimReference(e.target.value || null));
                    }}
                />
            </Card>

            {/* ── Card 6: Bulk reason code ── */}
            <Card justify="between">
                <CardLabel>{t('setReasonCodeForAllItems', 'Reason code (all items)')}</CardLabel>

                <StyledSelect
                    id="bulk-reason-select"
                    options={reasonCodes}
                    placeholder={t('selectReason', 'Select reason…')}
                    isDisabled={!buttonStates.canModify}
                    value={reasonCodes.find((opt) => opt.value === bulkReasonCode) || null}
                    onChange={(selectedOption) => {
                        const { value } = selectedOption as { label: string; value: number };
                        handleBulkReasonChange(value);
                    }}
                />
            </Card>
        </StyledGrid>
    );
};

export default MetaCards;

const StyledGrid = styled(Grid)`
    width: 100%;
    margin: 1rem 0rem;
`;

const Card = styled(Flex)`
    background: #f4f4f4;
    border-radius: var(--radius);
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const CardLabel = styled.span`
    font-size: 11px;
    color: ${({ theme }) => theme.colors.textLight};
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const DatePickerWrapper = styled.div`
    .react-datepicker-wrapper,
    .react-datepicker__input-container {
        width: 100%;
    }

    .selected {
        background-color: ${({ theme }) => theme.colors.main} !important;
        color: #fff !important;
    }

    input {
        width: 100%;
        border: 1px solid ${({ theme }) => theme.colors.border} !important;
        border-radius: var(--radius);
        padding: 0.5rem 0.5rem !important;
        font-size: 15px !important;
        letter-spacing: 0.02em !important;
        font-weight: 500 !important;
        background-color: ${({ theme }) => theme.colors.fillWhite} !important;

        cursor: pointer;

        &:disabled {
            color: ${({ theme }) => theme.colors.textDisabled} !important;
            cursor: not-allowed !important;
        }
    }

    & * {
        color: ${({ theme }) => theme.colors.text} !important;
    }
`;

const StyledSelect = styled(Select)`
    & * {
        color: ${({ theme }) => theme.colors.text} !important;
    }
`;

const StyledInput = styled.input`
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: var(--radius);
    padding: 0.5rem;
    font-size: 14px;
    background-color: ${({ theme }) => theme.colors.fillWhite};
    color: ${({ theme }) => theme.colors.text};

    &:disabled {
        color: ${({ theme }) => theme.colors.textDisabled};
        cursor: not-allowed;
    }
`;
